import { create } from 'zustand';
import api from '../lib/api';
import { toast } from '../components/ui/sonner';

/**
 * @typedef {'idle'|'speaking'|'reviewing'} SphereState
 * @typedef {{turn_number:number, role:'ai'|'student', text:string, is_intro?:boolean}} TranscriptEntry
 */

const useInterviewStore = create((set, get) => ({
  /** Session data from API */
  session: null,
  loading: true,
  error: null,

  /** Conversation */
  transcript: [],
  currentTurn: 0,
  isInterviewOver: false,
  aiResponding: false,
  /** Hard in-flight flag: blocks concurrent /turn submissions atomically. */
  submitInFlight: false,

  /** Sphere visual state: idle | speaking | reviewing */
  sphereState: 'idle',

  /** Media — camera starts off; the pre-interview consent gate turns it on
   * explicitly once the student agrees to be monitored. */
  cameraOn: false,
  micOn: true,

  // ── Actions ──

  setSphereState: (state) => set({ sphereState: state }),
  setCameraOn: (v) => set({ cameraOn: v }),
  setMicOn: (v) => set({ micOn: v }),

  /** Load session data */
  loadSession: async (sessionId) => {
    set({ loading: true, error: null });
    try {
      const { data } = await api.get(`/sessions/${sessionId}`);
      set({ session: data, loading: false });
    } catch (err) {
      set({ error: err?.response?.data?.detail || 'Failed to load session', loading: false });
    }
  },

  /** Get the opening AI message (turn 0) */
  startInterview: async (sessionId) => {
    set({ aiResponding: true });
    try {
      const { data } = await api.post(`/interview/${sessionId}/start`);
      set((s) => ({
        transcript: [...s.transcript, { turn_number: 0, role: 'ai', text: data.interviewer_text, is_intro: !!data.is_intro }],
        currentTurn: 1,
        aiResponding: false,
      }));
      return data.interviewer_text;
    } catch (err) {
      set({ aiResponding: false, error: err?.response?.data?.detail || 'Failed to start' });
      return null;
    }
  },

  /** Submit student text, get AI response. Hard-guarded against concurrent calls. */
  submitAnswer: async (sessionId, text) => {
    if (!text.trim()) return null;
    // Atomic in-flight check using zustand get() — prevents the click → re-render race
    // where a double-click slips through before `aiResponding` reaches the UI.
    if (get().submitInFlight) return null;
    set((s) => ({
      submitInFlight: true,
      transcript: [...s.transcript, { turn_number: s.currentTurn, role: 'student', text }],
      aiResponding: true,
    }));
    try {
      const { data } = await api.post(`/interview/${sessionId}/turn`, { text });
      set((s) => ({
        transcript: [...s.transcript, { turn_number: data.turn_number, role: 'ai', text: data.interviewer_text }],
        currentTurn: data.turn_number + 1,
        isInterviewOver: data.is_final,
        session: s.session ? { ...s.session, current_question_index: data.current_question_index } : s.session,
        aiResponding: false,
        submitInFlight: false,
      }));
      return data.interviewer_text;
    } catch (err) {
      set((s) => ({
        // Roll back the optimistic student entry — no AI reply ever came for
        // it, so leaving it in would show a duplicate on the next retry.
        transcript: s.transcript.slice(0, -1),
        aiResponding: false,
        submitInFlight: false,
      }));
      toast.error(err?.response?.data?.detail || 'Failed to submit your answer. Please try again.');
      return null;
    }
  },

  /** Submit audio blob via STT endpoint, get AI response. Same guard as submitAnswer. */
  submitAudioAnswer: async (sessionId, audioBlob) => {
    if (get().submitInFlight) return null;
    set({ submitInFlight: true, aiResponding: true });
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      const { data } = await api.post(`/interview/${sessionId}/audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      set((s) => ({
        transcript: [
          ...s.transcript,
          { turn_number: s.currentTurn, role: 'student', text: data.transcribed_text },
          { turn_number: data.turn_number, role: 'ai', text: data.interviewer_text },
        ],
        currentTurn: data.turn_number + 1,
        isInterviewOver: data.is_final,
        session: s.session ? { ...s.session, current_question_index: data.current_question_index } : s.session,
        aiResponding: false,
        submitInFlight: false,
      }));
      return data.interviewer_text;
    } catch (err) {
      set({ aiResponding: false, submitInFlight: false });
      toast.error(err?.response?.data?.detail || 'Failed to submit your answer. Please try again.');
      return null;
    }
  },

  /**
   * Commit a turn produced by the streaming STT WebSocket flow (transcription
   * + AI response already resolved server-side) into the transcript.
   * Same resulting shape as submitAudioAnswer's success path.
   */
  commitStreamedTurn: ({ transcribedText, turnNumber, interviewerText, isFinal, currentQuestionIndex }) => {
    set((s) => ({
      transcript: [
        ...s.transcript,
        { turn_number: s.currentTurn, role: 'student', text: transcribedText },
        { turn_number: turnNumber, role: 'ai', text: interviewerText },
      ],
      currentTurn: turnNumber + 1,
      isInterviewOver: isFinal,
      session: s.session ? { ...s.session, current_question_index: currentQuestionIndex } : s.session,
      aiResponding: false,
      submitInFlight: false,
    }));
  },

  setAiResponding: (value) => set({ aiResponding: value }),

  /** End interview early */
  endInterview: async (sessionId) => {
    try {
      await api.post(`/interview/${sessionId}/end`);
      set({ isInterviewOver: true });
    } catch {
      // ignore
    }
  },

  /** Reset store */
  reset: () => set({
    session: null, loading: true, error: null,
    transcript: [], currentTurn: 0, isInterviewOver: false, aiResponding: false,
    submitInFlight: false,
    sphereState: 'idle', cameraOn: false, micOn: true,
  }),
}));

export default useInterviewStore;
